import { useState, useMemo } from 'react'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(isoWeek)

interface UseWeekNavigationResult {
  currentWeek: string
  weekDisplayText: string
  canGoBack: boolean
  canGoForward: boolean
  goToPreviousWeek: () => void
  goToNextWeek: () => void
  goToCurrentWeek: () => void
}

export let useWeekNavigation = (): UseWeekNavigationResult => {
  // Start with current week (Monday of this week)
  let [currentWeek, setCurrentWeek] = useState(() =>
    dayjs().startOf('isoWeek').format('YYYY-MM-DD'),
  )

  let currentWeekDate = useMemo(() => dayjs(currentWeek), [currentWeek])
  let today = useMemo(() => dayjs(), [])

  // Calculate 4 weeks ago limit (API restriction)
  let fourWeeksAgo = useMemo(
    () => today.subtract(4, 'week').startOf('isoWeek'),
    [today],
  )

  let canGoBack = useMemo(
    () => currentWeekDate.isAfter(fourWeeksAgo),
    [currentWeekDate, fourWeeksAgo],
  )

  // No limit on going forward
  let canGoForward = true

  let weekDisplayText = useMemo(() => {
    let startDate = currentWeekDate
    let endDate = currentWeekDate.add(6, 'day')

    // Format: "December 16 - 22, 2024"
    if (startDate.month() === endDate.month()) {
      return `${startDate.format('MMMM D')} - ${endDate.format('D, YYYY')}`
    }
    return `${startDate.format('MMMM D')} - ${endDate.format('MMMM D, YYYY')}`
  }, [currentWeekDate])

  let goToPreviousWeek = () => {
    if (canGoBack) {
      let previousWeek = currentWeekDate
        .subtract(1, 'week')
        .format('YYYY-MM-DD')
      setCurrentWeek(previousWeek)
    }
  }

  let goToNextWeek = () => {
    let nextWeek = currentWeekDate.add(1, 'week').format('YYYY-MM-DD')
    setCurrentWeek(nextWeek)
  }

  let goToCurrentWeek = () => {
    setCurrentWeek(today.startOf('isoWeek').format('YYYY-MM-DD'))
  }

  return {
    currentWeek,
    weekDisplayText,
    canGoBack,
    canGoForward,
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek,
  }
}
